import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { Spinner } from '@/components/atoms/Spinner';
import PageLayout from '@/components/Layout/PageLayout';
import ExportDataDropDown from '@/components/molecules/ExportDataDropDown';
import DownloadOptions from '@/components/molecules/ExportDataDropDown/DownloadOptions';
import { useCage, useCageName } from '@/contexts/cage';
import { useCageFilter } from '@/contexts/cage-filter-context';
import { enrichBodyWoundsDataWithTemperature } from '@/domain/welfare/enrichBodyWoundsWithTemperature';
import type { DailyWelfareData, EnrichedBodyWoundsData } from '@/domain/welfare/types';
import { useGetCageTemperature } from '@/domain/welfare/useGetCageTemperature';
import { useGetFullWelfare } from '@/domain/welfare/useGetFullWelfare';
import { useDateRangePicker } from '@/hooks/useDateRangePicker';
import { useQueryParams } from '@/hooks/useQueryParams';
import { CageWelfareGraphsPageContent } from '@/routes/Client/Cage/pages/CageWelfare/CageWelfareWounds/pages/CageWelfareGraphs/CageWelfareGraphsPageContent';
import { HEALTH_DEFAULT_DATE_RANGE_PRESET_ID } from '@/routes/Client/Cage/pages/Health/components/healthDetailChartConstants';
import type { CagePageInterface } from '@/routes/Client/Cage/utils/cage-pages-interface';
import { formatMachineNumber } from '@/utils/localeFormatters';
import type { ParsedQuery } from '@/utils/queryParams';

const downloadableData = (
    dailyHealthData: DailyWelfareData[],
    queryParams?: ParsedQuery<string>
) => {
    const DEFAULT_DAYPART_TAG_FOR_DOWNLOAD = 'day';
    const daypartTagForDownload = queryParams?.d ? queryParams.d : DEFAULT_DAYPART_TAG_FOR_DOWNLOAD;
    const dailyHealthDataForDownload =
        dailyHealthData?.map((el) => ({
            date: el?.day,
            daypartTag: daypartTagForDownload,
            count: el?.count,
            wound: formatMachineNumber(el?.wound, 2),
            scaleLoss: formatMachineNumber(el?.skinSpeckles, 2),
            smallWound: formatMachineNumber(el?.smallWound, 2),
            mediumWound: formatMachineNumber(el?.mediumWound, 2),
            bigWound: formatMachineNumber(el?.bigWound, 2),
            singleWound: formatMachineNumber(el?.oneWound, 2),
            moreThanOneWound: formatMachineNumber(el?.moreThanOneWound, 2),
        })) ?? [];
    const locationInfoColumns = [
        'location.name',
        'location.timezone',
        'location.sWeightPct',
        'cage.name',
    ];
    const dataColumnNames =
        dailyHealthDataForDownload.length > 0 ? Object.keys(dailyHealthDataForDownload[0]) : [];
    const formattedDataColumnNames = dataColumnNames.map((e) => `day.${e}`);
    const allColumnNames = [...locationInfoColumns, ...formattedDataColumnNames];
    return { dailyHealthDataForDownload, allColumnNames };
};

function CageWelfareGraphsContainer({ cageId }: CagePageInterface) {
    const { t } = useTranslation();

    const { loading: cageLoading } = useCage();
    const cageName = useCageName();
    const { daypartTag, selectedFishGroupId } = useCageFilter();
    const { dateFrom, dateTo, onDateChange } = useDateRangePicker({
        key: 'wounds-graphs',
        defaultPresetId: HEALTH_DEFAULT_DATE_RANGE_PRESET_ID,
    });

    const welfareParams = useMemo(
        () => ({
            from: dateFrom,
            to: dateTo,
            daypartTag: daypartTag || 'day',
            fishGroupId: selectedFishGroupId,
        }),
        [dateFrom, dateTo, daypartTag, selectedFishGroupId]
    );

    const { data: fishHealth, isLoading: fishHealthLoading } = useGetFullWelfare(
        cageId,
        welfareParams,
        {
            enabled: Boolean(dateFrom && dateTo),
            staleTime: 5 * 60 * 1000,
        }
    );

    const { data: temperature } = useGetCageTemperature(cageId, {
        daypartTag: daypartTag || 'day',
        weightUnitTag: 'g',
    });

    const loading = cageLoading || fishHealthLoading;

    const enrichedBodyWoundsData: EnrichedBodyWoundsData | undefined = useMemo(() => {
        if (!fishHealth || !fishHealth.bodyWoundsData) {
            return undefined;
        }
        return enrichBodyWoundsDataWithTemperature(fishHealth.bodyWoundsData, temperature);
    }, [fishHealth, temperature]);

    const [queryParams] = useQueryParams();
    const { dailyHealthDataForDownload, allColumnNames } = downloadableData(
        fishHealth?.welfareData?.days ?? [],
        queryParams
    );

    if (loading || !dateFrom || !dateTo) {
        return <Spinner size="lg" fullScreen />;
    }

    return (
        <PageLayout
            title={cageName}
            subtitle={t('common.labels.graphs')}
            width="wide"
            actions={
                <ExportDataDropDown>
                    <DownloadOptions data={dailyHealthDataForDownload} columns={allColumnNames} />
                </ExportDataDropDown>
            }>
            <CageWelfareGraphsPageContent
                fishHealthData={enrichedBodyWoundsData}
                dateFrom={dateFrom}
                dateTo={dateTo}
                onDateChange={onDateChange}
            />
        </PageLayout>
    );
}

export default CageWelfareGraphsContainer;
export { CageWelfareGraphsContainer };
